import { useEffect, useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { onSessionExpired } from '@shared/auth';
import { createQueryClient } from './query-client';

export interface QueryProviderProps {
  children: ReactNode;
  /**
   * 是否掛上 devtools 面板。傳 `import.meta.env.DEV`。
   *
   * 由呼叫端決定而不是在這裡讀 `import.meta.env`：那是打包器提供的全域，寫進
   * framework:agnostic 的 lib 等於假設了打包器（Storybook、測試環境未必有）。
   * 這與 `registerServiceWorker` 收 `disabled` 而非自己判斷是同一個作法。
   */
  devtools?: boolean;
}

/**
 * TanStack Query 的接線。
 *
 * 負責三件事：建立 client、在 session 失效時清空快取、（開發時）掛上 devtools。
 *
 * **client 建在 state 裡而不是模組層級。**模組層級的單例會在熱更新時被保留、
 * 卻與新的元件樹對不上，而且測試之間會共用同一份快取。`useState` 的初始化函式只在
 * 第一次渲染執行一次，正好是「每個 app 實例一個 client」的語意。
 */
export function QueryProvider({
  children,
  devtools = false,
}: QueryProviderProps) {
  const [queryClient] = useState(createQueryClient);

  /*
   * session 失效時清空快取。
   *
   * 快取活在 client 裡、不隨畫面卸載消失，不清的話重新登入（甚至換一個人登入）
   * 之後，畫面會先閃一輪上一位使用者的資料——那些資料連重新驗證都還沒開始。
   *
   * `clear()` 而非 `invalidateQueries()`：後者只是標記過期，資料仍留著並繼續顯示。
   */
  useEffect(() => onSessionExpired(() => queryClient.clear()), [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/*
        devtools 在正式建置會是一個回傳 null 的空元件（套件自己以 NODE_ENV 判斷），
        故不需要動態 import——那反而會多出一個永遠不會被載入、卻會被 Service Worker
        precache 起來的 chunk。
      */}
      {devtools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
